import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
    Monitor,
    Bell,
    BellOff,
    MonitorOff,
    Settings,
    X,
    BookOpen,
    MessageSquare,
} from "lucide-react";
import {
    requestWakeLock,
    releaseWakeLock,
    isWakeLockActive,
} from "@/utils/wakeLock";
import {
    requestNotificationPermission,
    getNotificationPermission,
    isNotificationSupported,
    sendNotification,
} from "@/utils/notifications";
import type { ReflectionSettings } from "@/types";
import { DEFAULT_REFLECTION_SETTINGS } from "@/types";

interface SettingsPanelProps {
    isOpen: boolean;
    onClose: () => void;
    onShowTutorial?: () => void;
    reflectionSettings: ReflectionSettings;
    onReflectionSettingsChange: (settings: ReflectionSettings) => void;
}

export function SettingsPanel({
    isOpen,
    onClose,
    onShowTutorial,
    reflectionSettings,
    onReflectionSettingsChange,
}: SettingsPanelProps) {
    const [wakeLockEnabled, setWakeLockEnabled] = useState(false);
    const [notificationPermission, setNotificationPermission] =
        useState<string>("default");
    const [notificationSupported] = useState(isNotificationSupported());
    const [intervalInput, setIntervalInput] = useState(
        reflectionSettings.intervalMinutes.toString()
    );

    useEffect(() => {
        setWakeLockEnabled(isWakeLockActive());
        setNotificationPermission(getNotificationPermission());
        setIntervalInput(reflectionSettings.intervalMinutes.toString());
    }, [isOpen, reflectionSettings.intervalMinutes]);

    const toggleWakeLock = async () => {
        if (wakeLockEnabled) {
            await releaseWakeLock();
            setWakeLockEnabled(false);
            localStorage.setItem("taskmaster-wakelock", "false");
        } else {
            const success = await requestWakeLock();
            setWakeLockEnabled(success);
            if (success) {
                localStorage.setItem("taskmaster-wakelock", "true");
            }
        }
    };

    const handleNotificationPermission = async () => {
        const permission = await requestNotificationPermission();
        setNotificationPermission(permission);
        if (permission === "granted") {
            localStorage.setItem("taskmaster-notifications", "true");
            // Send a test notification
            sendNotification("🎉 Notifications Enabled!", {
                body: "You'll now receive alerts when tasks go overtime.",
            });
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md border-0 shadow-2xl">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="flex items-center gap-2">
                        <Settings className="w-5 h-5" />
                        Settings
                    </CardTitle>
                    <Button variant="ghost" size="icon" onClick={onClose}>
                        <X className="w-4 h-4" />
                    </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* Wake Lock Setting */}
                    <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 dark:bg-slate-800">
                        <div className="flex items-center gap-3">
                            {wakeLockEnabled ? (
                                <Monitor className="w-5 h-5 text-green-500" />
                            ) : (
                                <MonitorOff className="w-5 h-5 text-slate-400" />
                            )}
                            <div>
                                <div className="font-medium">
                                    Keep Screen Awake
                                </div>
                                <div className="text-xs text-slate-500 dark:text-slate-400">
                                    {wakeLockEnabled
                                        ? "Screen will stay on during tasks"
                                        : "Tap to prevent screen from dimming"}
                                </div>
                            </div>
                        </div>
                        <Button
                            variant={wakeLockEnabled ? "default" : "outline"}
                            size="sm"
                            onClick={toggleWakeLock}
                        >
                            {wakeLockEnabled ? "On" : "Off"}
                        </Button>
                    </div>

                    {/* Notifications Setting */}
                    <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 dark:bg-slate-800">
                        <div className="flex items-center gap-3">
                            {notificationPermission === "granted" ? (
                                <Bell className="w-5 h-5 text-green-500" />
                            ) : (
                                <BellOff className="w-5 h-5 text-slate-400" />
                            )}
                            <div>
                                <div className="font-medium">Notifications</div>
                                <div className="text-xs text-slate-500 dark:text-slate-400">
                                    {!notificationSupported
                                        ? "Not supported in this browser"
                                        : notificationPermission === "denied"
                                        ? "Blocked - enable in browser settings"
                                        : "Get alerts when tasks go overtime"}
                                </div>
                            </div>
                        </div>
                        {notificationPermission === "granted" ? (
                            <div className="flex items-center gap-2">
                                <Badge
                                    variant="secondary"
                                    className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                >
                                    Enabled
                                </Badge>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        const result = sendNotification(
                                            "🔔 Test Notification",
                                            {
                                                body: "Notifications are working! You'll receive alerts when tasks go overtime.",
                                            }
                                        );
                                        if (!result) {
                                            // Fallback: try creating notification directly
                                            try {
                                                new Notification(
                                                    "🔔 Test Notification",
                                                    {
                                                        body: "Notifications are working! You'll receive alerts when tasks go overtime.",
                                                    }
                                                );
                                            } catch (e) {
                                                console.error(
                                                    "Notification failed:",
                                                    e
                                                );
                                                alert(
                                                    "Notifications may be blocked by your browser. Please check your browser settings."
                                                );
                                            }
                                        }
                                    }}
                                >
                                    Test
                                </Button>
                            </div>
                        ) : (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleNotificationPermission}
                                disabled={
                                    !notificationSupported ||
                                    notificationPermission === "denied"
                                }
                            >
                                Enable
                            </Button>
                        )}
                    </div>

                    {/* Tutorial Button */}
                    <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 dark:bg-slate-800">
                        <div className="flex items-center gap-3">
                            <BookOpen className="w-5 h-5 text-violet-500" />
                            <div>
                                <div className="font-medium">Tutorial</div>
                                <div className="text-xs text-slate-500 dark:text-slate-400">
                                    Learn how to use TaskMaster
                                </div>
                            </div>
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                onShowTutorial?.();
                                onClose();
                            }}
                        >
                            Replay
                        </Button>
                    </div>

                    {/* Reflection Prompts Setting */}
                    <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800 space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <MessageSquare
                                    className={`w-5 h-5 ${
                                        reflectionSettings.enabled
                                            ? "text-blue-500"
                                            : "text-slate-400"
                                    }`}
                                />
                                <div>
                                    <div className="font-medium">
                                        Reflection Prompts
                                    </div>
                                    <div className="text-xs text-slate-500 dark:text-slate-400">
                                        Periodic check-ins to note progress
                                    </div>
                                </div>
                            </div>
                            <Button
                                variant={
                                    reflectionSettings.enabled
                                        ? "default"
                                        : "outline"
                                }
                                size="sm"
                                onClick={() =>
                                    onReflectionSettingsChange({
                                        ...reflectionSettings,
                                        enabled: !reflectionSettings.enabled,
                                    })
                                }
                            >
                                {reflectionSettings.enabled ? "On" : "Off"}
                            </Button>
                        </div>
                        {reflectionSettings.enabled && (
                            <div className="flex items-center gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                                <span className="text-sm text-muted-foreground">
                                    Prompt every
                                </span>
                                <Input
                                    type="number"
                                    min="1"
                                    max="60"
                                    value={intervalInput}
                                    onChange={(e) => {
                                        setIntervalInput(e.target.value);
                                        const val = parseInt(e.target.value);
                                        if (val >= 1 && val <= 60) {
                                            onReflectionSettingsChange({
                                                ...reflectionSettings,
                                                intervalMinutes: val,
                                            });
                                        }
                                    }}
                                    className="w-16 h-8 text-center"
                                />
                                <span className="text-sm text-muted-foreground">
                                    minutes
                                </span>
                            </div>
                        )}
                    </div>

                    <p className="text-xs text-center text-slate-500 dark:text-slate-400 pt-2">
                        These settings help you stay focused during tasks
                    </p>
                </CardContent>
            </Card>
        </div>
    );
}
